import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { MAIL, RevealItem, Rise, mailto } from "./shared.jsx";
import "./join.css";

const FORMATS = [
  ["Host a programme or workshop", "An introductory session or a project activity with your students, planned around your timetable and space."],
  ["Nominate student teams", "Put teams forward for a GRID programme once it is announced with dates and eligibility."],
  ["Share verified local problems", "Point students to real needs in your community that they can investigate with support."],
  ["Connect teachers and mentors", "Bring in staff or mentors who can guide students while they build."],
];

const ACTIVITIES = ["An introductory session", "Taking part in an announced programme", "A project activity", "Not sure yet"];

const FIELDS = [
  { key: "school", label: "School and location", placeholder: "School name, city", line: (v) => `We are ${v}.` },
  { key: "ages", label: "Student age range", placeholder: "For example, 13 to 16", line: (v) => `Student age range: ${v}.` },
  { key: "size", label: "Approximate group size", placeholder: "For example, 40 students", line: (v) => `Approximate group size: ${v}.` },
  { key: "activity", label: "Activity you have in mind", select: ACTIVITIES, line: (v) => `Activity we have in mind: ${v}.` },
  { key: "support", label: "Space, equipment and staff support", placeholder: "Rooms, devices, teachers who can help", textarea: true, line: (v) => `Space, equipment and staff support: ${v}.` },
  { key: "dates", label: "Preferred dates", placeholder: "A week or month is fine", line: (v) => `Preferred dates: ${v}.` },
  { key: "contact", label: "Teacher or representative", placeholder: "Name, role and email", line: (v) => `Contact: ${v}.` },
];

function PreviewLine({ value, empty, children }) {
  const reduce = useReducedMotion();
  return (
    <p className={`jn-preview__line ${value ? "is-filled" : ""}`}>
      {value ? (
        <motion.mark
          initial={reduce ? false : { backgroundColor: "rgba(169, 215, 255, 1)" }}
          animate={{ backgroundColor: "rgba(169, 215, 255, 0)" }}
          transition={{ duration: 1.4, ease: EASE_OUT }}
        >
          {children}
        </motion.mark>
      ) : (
        <span className="jn-preview__empty">{empty}</span>
      )}
    </p>
  );
}

function EnquiryBuilder() {
  const [values, setValues] = useState({ activity: ACTIVITIES[0] });
  const set = (key) => (event) => setValues({ ...values, [key]: event.target.value });
  const filled = FIELDS.filter((field) => values[field.key]).length;
  const body = [
    "Hi Project GRID,",
    "",
    ...FIELDS.map((field) => field.line(values[field.key] || `[${field.label.toLowerCase()}]`)),
    "",
    "Thank you.",
  ].join("\n");

  return (
    <section className="jn-sc-build" id="enquiry" aria-labelledby="jn-sc-build-title">
      <div className="jn-wrap">
        <header className="jn-sc-build__head">
          <MaskWords id="jn-sc-build-title" className="jn-h2" text="What we need from your school." />
          <p className="jn-lede">
            Fill in what you know. The email on the right writes itself as you type. Nothing is sent from this page. The button opens your own email app with this text, addressed to {MAIL}.
          </p>
        </header>
        <div className="jn-sc-build__grid">
          <form className="jn-sc-form" onSubmit={(event) => event.preventDefault()}>
            {FIELDS.map((field) => (
              <label key={field.key} className="jn-field">
                <span>{field.label}</span>
                {field.select ? (
                  <select value={values[field.key]} onChange={set(field.key)}>
                    {field.select.map((option) => <option key={option}>{option}</option>)}
                  </select>
                ) : field.textarea ? (
                  <textarea rows={3} value={values[field.key] ?? ""} onChange={set(field.key)} placeholder={field.placeholder} />
                ) : (
                  <input value={values[field.key] ?? ""} onChange={set(field.key)} placeholder={field.placeholder} />
                )}
              </label>
            ))}
          </form>
          <div className="jn-preview" aria-label="Email preview">
            <div className="jn-preview__meta">
              <p><span className="jn-mono">To</span>{MAIL}</p>
              <p><span className="jn-mono">Subject</span>School enquiry</p>
            </div>
            <div className="jn-preview__body">
              <p className="jn-preview__line is-filled">Hi Project GRID,</p>
              {FIELDS.map((field) => (
                <Fragment key={field.key}>
                  <PreviewLine value={values[field.key]} empty={field.line(`[${field.label.toLowerCase()}]`)}>
                    {field.line(values[field.key])}
                  </PreviewLine>
                </Fragment>
              ))}
            </div>
            <div className="jn-preview__foot">
              <span className="jn-small">{filled} of {FIELDS.length} details added</span>
              <a className="gh-btn gh-btn--fill" href={mailto("School enquiry", body)}>
                Open this email <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const NOTES = [
  ["Nothing is confirmed until the scope is agreed.", "Format, cost, supervision and accessibility arrangements must be agreed before an activity is confirmed. These are enquiry topics, not guaranteed services."],
  ["Each kind of activity needs its own plan.", "An introductory session, participation in an announced programme or a project activity may each need a different plan."],
  ["You will know who is responsible.", "GRID is run by school students. The team page lists named roles, and our governance page covers responsibility, safety and policies."],
  ["Credit stays accurate.", "Every programme page says who led it and what GRID did. We will not describe your school's part as more, or less, than it was."],
];

export default function ForSchoolsPage() {
  return (
    <PageFrame className="rd-page jn-page jn-sc">
      <Seo
        title="For schools"
        path="/for-schools"
        description="Help students work on real needs with clear structure, accessible tools and a path beyond one competition day."
      />
      <main>
        <header className="jn-wrap jn-sc-hero">
          <Rise as="p" className="jn-mono" delay={0.05}>For schools</Rise>
          <MaskWords as="h1" className="jn-h1 jn-sc-hero__title" text="Bring project work to your students." onMount delay={0.1} />
          <Rise className="jn-sc-hero__row" delay={0.4}>
            <p className="jn-lede">
              Help students work on real needs with clear structure, accessible tools and a path beyond one competition day. Tell us what your students need and what your school can support.
            </p>
            <div className="gh-actions">
              <a className="gh-btn gh-btn--fill" href="#enquiry"><span className="roll" data-label="Prepare a school enquiry"><span>Prepare a school enquiry</span></span></a>
              <a className="gh-btn gh-btn--ghost" href={mailto("School enquiry")}><span className="roll" data-label="Email us directly"><span>Email us directly</span></span></a>
            </div>
          </Rise>
        </header>
        <div className="jn-wrap jn-sc-hero__photo">
          <ClipPhoto
            src="/images/grid-photo-01-p1.jpg"
            alt="Students gathered at the Metamorphosis programme at IIT Delhi"
            caption="Metamorphosis at IIT Delhi, led by Tale of Humankind Foundation and IIT Delhi's outreach office. GRID was the outreach partner."
            sizes="100vw"
            priority
          />
        </div>

        <section className="jn-wrap jn-sc-formats" aria-labelledby="jn-sc-formats-title">
          <div className="jn-sc-formats__head">
            <MaskWords id="jn-sc-formats-title" className="jn-h2" text="What a collaboration can look like." />
            <p className="jn-lede">Four shapes we can discuss. Your setting decides which one fits.</p>
          </div>
          <dl className="jn-sc-formats__list">
            {FORMATS.map(([term, text], index) => (
              <RevealItem as="div" key={term} className="jn-sc-formats__row" delay={index * 0.06}>
                <dt>{term}</dt>
                <dd>{text}</dd>
              </RevealItem>
            ))}
          </dl>
        </section>

        <EnquiryBuilder />

        <section className="jn-wrap jn-sc-notes" aria-labelledby="jn-sc-notes-title">
          <MaskWords id="jn-sc-notes-title" className="jn-h2" text="Safeguarding and responsibility." />
          <ul>
            {NOTES.map(([title, text]) => (
              <li key={title}>
                <strong>{title}</strong>
                <p>{text}</p>
              </li>
            ))}
          </ul>
          <div className="gh-actions">
            <Link className="gh-link" to="/governance">How GRID is run <ArrowRight weight="bold" aria-hidden="true" /></Link>
            <Link className="gh-link" to="/team">The team <ArrowRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </section>

        <section className="jn-wrap jn-sc-prev" aria-labelledby="jn-sc-prev-title">
          <h2 id="jn-sc-prev-title" className="jn-mono">See previous work first</h2>
          <div className="jn-sc-prev__links">
            <Link to="/programmes/aaroh"><strong>AAROH recap</strong><span>363 young innovators at IIIT Delhi over two days</span><ArrowUpRight weight="bold" aria-hidden="true" /></Link>
            <Link to="/impact"><strong>Our impact</strong><span>2,013 participants across AAROH, Aurora and Metamorphosis</span><ArrowUpRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
